import React, { useEffect, useState, useRef } from "react";
import { get, del } from "../../common/apiClient";
import { useNavigate, useParams } from "react-router-dom";
import SidebarCreateMailbox from "./SidebarCreateMailbox";
import SidebarMailboxActions from "./SidebarMailboxActions";
import InboxList from "./InboxList";
import MailViewer from "./MailViewer";
import EmptyState from "./EmptyState";
import LoadingState from "./LoadingState";
import LanguageSelect from "./LanguageSelect";
import { localStorageGetItem, localStorageSetItem } from "../../common/localStorage";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const MailboxPage = () => {
    const { address: routeAddress } = useParams();
    const navigate = useNavigate();

    const [mailbox, setMailbox] = useState(null);
    const [messages, setMessages] = useState([]);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingMessageDetail, setLoadingMessageDetail] = useState(false);
    const [mobilePane, setMobilePane] = useState("inbox");
    const sidebarCreateMailboxRef = useRef(null);

    useEffect(() => {
        const stored = localStorageGetItem("currentMailbox");
        const currentRouteAddress = routeAddress
            ? decodeURIComponent(routeAddress)
            : null;

        if (currentRouteAddress) {
            setMailbox((prev) => {
                if (prev?.address === currentRouteAddress) {
                    return prev;
                }
                return {
                    ...(prev || {}),
                    address: currentRouteAddress,
                };
            });
            if (stored && stored.address === currentRouteAddress) {
                setMailbox(stored);
                fetchMessagesByAddress(currentRouteAddress);
            } else {
                resolveMailboxByAddress(currentRouteAddress);
            }
        } else if (stored && stored.address) {
            setMailbox(stored);
            fetchMessagesByAddress(stored.address);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeAddress]);

    useEffect(() => {
        if (!mailbox) return;

        const handler = () => {
            if (mailbox.address) {
                fetchMessagesByAddress(mailbox.address);
            } else if (mailbox.id) {
                fetchMessages(mailbox.id);
            }
        };

        // Poll every 5 seconds for new messages
        const interval = setInterval(() => {
            handler();
        }, 5000);

        return () => {
            clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mailbox]);

    const fetchMessages = async (mailboxId) => {
        setLoadingMessages(true);
        try {
            const res = await get(`/public/mailboxes/${mailboxId}/messages`);
            const data = res.data || res;
            setMessages(data);
            if (data.length === 0) {
                setSelectedMessageId(null);
                setSelectedMessage(null);
            } else if (!selectedMessageId) {
                setSelectedMessageId(data[0].id);
                fetchMessageDetail(data[0].id);
            }
        } catch (err) {
            console.error("Failed to load messages", err);
            toast.error("Failed to load messages");
        } finally {
            setLoadingMessages(false);
        }
    };

    const fetchMessagesByAddress = async (address) => {
        if (!address) return;
        setLoadingMessages(true);
        try {
            const encoded = encodeURIComponent(address);
            const res = await get(
                `/public/mailboxes/by-address/${encoded}/messages`
            );
            const data = res.data || res;
            setMessages(data);
            if (data.length === 0) {
                setSelectedMessageId(null);
                setSelectedMessage(null);
            } else if (!selectedMessageId) {
                setSelectedMessageId(data[0].id);
                fetchMessageDetail(data[0].id);
            }
        } catch (err) {
            console.error("Failed to load messages by address", err);
            toast.error("Failed to load messages");
        } finally {
            setLoadingMessages(false);
        }
    };

    const resolveMailboxByAddress = async (address) => {
        try {
            const res = await get(
                `/public/mailboxes/by-address/${encodeURIComponent(address)}/messages`
            );
            const data = res.data || res;
            const mb = {
                id: data.id,
                address: data.address || address,
            };
            setMailbox(mb);
            localStorageSetItem("currentMailbox", mb);
            fetchMessagesByAddress(address);
        } catch (err) {
            console.error("Resolve mailbox by address failed", err);
            setMailbox((prev) => ({
                ...(prev || {}),
                address,
            }));
        }
    };

    const fetchMessageDetail = async (messageId) => {
        setLoadingMessageDetail(true);
        try {
            const res = await get(`/public/messages/${messageId}`);
            const data = res.data || res;
            setSelectedMessage({
                id: data.id,
                from: data.from,
                to: data.to,
                subject: data.subject,
                text: data.text,
                html: data.html,
                attachments: (data.attachments || []).map((a) => ({
                    id: a.id,
                    filename: a.filename,
                    size: a.size,
                    contentType: a.contentType,
                })),
            });
        } catch (err) {
            console.error("Failed to load message", err);
            toast.error("Failed to load message");
        } finally {
            setLoadingMessageDetail(false);
        }
    };

    const handleMailboxCreated = (mb) => {
        setMailbox(mb);
        localStorageSetItem("currentMailbox", mb);
        setMessages([]);
        setSelectedMessageId(null);
        setSelectedMessage(null);
        setMobilePane("inbox");
        const encodedAddress = encodeURIComponent(mb.address);
        navigate(`/${encodedAddress}`);
        fetchMessagesByAddress(mb.address);
    };

    const handleRefresh = () => {
        if (!mailbox) return;
        if (mailbox.address) {
            fetchMessagesByAddress(mailbox.address);
        } else if (mailbox.id) {
            fetchMessages(mailbox.id);
        }
    };

    const handleNew = () => {
        setMailbox(null);
        setMessages([]);
        setSelectedMessageId(null);
        setSelectedMessage(null);
        setMobilePane("inbox");
        localStorageSetItem("currentMailbox", null);
    };

    const handleDeleteMailbox = async () => {
        if (!mailbox) return;
        try {
            await del(`/public/mailboxes/${mailbox.id}`);
            setMailbox(null);
            localStorageSetItem("currentMailbox", null);
            setMessages([]);
            setSelectedMessageId(null);
            setSelectedMessage(null);
            navigate("/");
            toast.success("Mailbox deleted");
            // Reset username input
            if (sidebarCreateMailboxRef.current) {
                sidebarCreateMailboxRef.current.resetForm();
            }
        } catch (err) {
            console.error("Delete mailbox failed", err);
            toast.error("Delete mailbox failed");
        } finally {
            handleNew();
        }
    };

    const handleSelectMessage = (id) => {
        setSelectedMessageId(id);
        fetchMessageDetail(id);
        setMobilePane("message");
    };

    const handleDeleteMessage = async () => {
        if (!selectedMessageId) return;
        try {
            await del(`/public/messages/${selectedMessageId}`);
            toast.success("Message deleted");
            if (mailbox) {
                fetchMessages(mailbox.id);
            }
        } catch (err) {
            console.error("Delete message failed", err);
            toast.error("Delete message failed");
        }
    };

    return (
        <div className="min-h-[calc(100vh-120px)] bg-slate-50 py-3 px-2 sm:py-4 sm:px-4 lg:py-6">
            <div className="max-w-[80rem] mx-auto bg-white rounded-xl shadow-md overflow-hidden flex flex-col lg:flex-row">
                <div className="w-full lg:w-[22rem] bg-blue-700 p-4 flex flex-col">
                    <SidebarCreateMailbox
                        ref={sidebarCreateMailboxRef}
                        onMailboxCreated={handleMailboxCreated}
                        currentMailbox={mailbox}
                    />
                    <SidebarMailboxActions
                        mailbox={mailbox}
                        onRefresh={handleRefresh}
                        onNew={handleNew}
                        onDelete={handleDeleteMailbox}
                    />
                </div>
                <div className="flex-1 flex flex-col">
                    <div className="px-4 py-3 border-b border-gray-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-sm font-semibold text-gray-700 break-all">
                            {mailbox ? mailbox.address : "No mailbox selected"}
                        </div>
                        <LanguageSelect />
                    </div>
                    <div className="md:hidden grid grid-cols-2 border-b border-gray-200">
                        <button
                            type="button"
                            onClick={() => setMobilePane("inbox")}
                            className={`py-2 text-sm font-medium ${mobilePane === "inbox" ? "bg-blue-50 text-blue-700" : "text-gray-600"}`}
                        >
                            Hộp thư đến
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobilePane("message")}
                            className={`py-2 text-sm font-medium ${mobilePane === "message" ? "bg-blue-50 text-blue-700" : "text-gray-600"}`}
                        >
                            Nội dung thư
                        </button>
                    </div>
                    <div className="flex flex-1 min-h-[55vh]">
                        <div
                            className={`w-full md:w-80 md:min-w-80 border-r border-gray-200 ${mobilePane === "message" ? "hidden md:block" : "block"}`}
                        >
                            {loadingMessages && !messages.length ? (
                                <LoadingState />
                            ) : (
                                <InboxList
                                    messages={messages}
                                    selectedId={selectedMessageId}
                                    onSelect={handleSelectMessage}
                                />
                            )}
                        </div>
                        <div className={`flex-1 ${mobilePane === "inbox" ? "hidden md:block" : "block"}`}>
                            {loadingMessageDetail && !selectedMessage ? (
                                <LoadingState />
                            ) : messages.length === 0 ? (
                                <EmptyState />
                            ) : (
                                <MailViewer
                                    mailbox={mailbox}
                                    message={selectedMessage}
                                    onDelete={handleDeleteMessage}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MailboxPage;

